/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';

const CANCEL_SHIPMENT_ONLY = 'CANCEL_SHIPMENT_ONLY';
const CANCEL_ENTIRE_ORDER = 'CANCEL_ENTIRE_ORDER';

const DEFAULT_REASON = 'Ship Later Review';

const OPTION_CONFIG = {
  [CANCEL_SHIPMENT_ONLY]: {
    label: 'Cancel Shipment Only',
    description:
      'Cancels the current shipment only. The order will remain active and can be reviewed, updated, or shipped again.',
  },
  [CANCEL_ENTIRE_ORDER]: {
    label: 'Cancel Entire Order',
    description:
      'Cancels the order and all associated items. The order will not continue in workflow.',
  },
};

const CancelShipmentDialog = ({
  open,
  onClose,
  shipmentId,
  onConfirm,
  isLoading,
  hideOrderCancelOption,
}) => {
  const [step, setStep] = useState(hideOrderCancelOption ? 'confirm' : 'choice');
  const [selectedOption, setSelectedOption] = useState(null);
  const [reason, setReason] = useState(DEFAULT_REASON);

  useEffect(() => {
    if (open) {
      setStep(hideOrderCancelOption ? 'confirm' : 'choice');
      setSelectedOption(null);
      setReason(DEFAULT_REASON);
    }
  }, [open, hideOrderCancelOption]);

  const handleClose = () => {
    if (!isLoading) {
      setStep(hideOrderCancelOption ? 'confirm' : 'choice');
      setSelectedOption(null);
      setReason(DEFAULT_REASON);

      onClose();
    }
  };

  const handleContinue = () => {
    if (selectedOption === CANCEL_SHIPMENT_ONLY) {
      setStep('reason');
    } else if (selectedOption === CANCEL_ENTIRE_ORDER) {
      setStep('confirm');
    }
  };

  const handleBack = () => {
    setStep('choice');
  };

  const handleConfirmCancelShipmentOnly = () => {
    onConfirm({ cancelOrder: false, reason });
  };

  const handleConfirmCancelOrder = () => {
    onConfirm({ cancelOrder: true });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>Cancel Shipment</DialogTitle>

      {hideOrderCancelOption && (
        <>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel shipment <strong>{shipmentId}</strong>? This action
              cannot be undone.
            </DialogContentText>
            <Alert severity='info' sx={{ mt: 2 }}>
              Canceling this shipment will send the order to Prescription Review with a &apos;Ship
              Later&apos; reason and tag.
            </Alert>
            <TextField
              label='Reason (optional)'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              fullWidth
              size='small'
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isLoading}>
              No
            </Button>
            <LoadingButton
              loading={isLoading}
              variant='contained'
              color='error'
              onClick={handleConfirmCancelShipmentOnly}
            >
              Yes, Cancel Shipment
            </LoadingButton>
          </DialogActions>
        </>
      )}

      {!hideOrderCancelOption && step === 'choice' && (
        <>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Shipment <strong>{shipmentId}</strong>. Choose how you want to cancel:
            </DialogContentText>
            <RadioGroup value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
              {Object.entries(OPTION_CONFIG).map(([value, { label, description }]) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  disabled={isLoading}
                  control={<Radio />}
                  label={
                    <>
                      <Typography variant='body1'>{label}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {description}
                      </Typography>
                    </>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
              ))}
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isLoading}>
              No
            </Button>
            <Button
              variant='contained'
              color='error'
              onClick={handleContinue}
              disabled={!selectedOption || isLoading}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {!hideOrderCancelOption && step === 'reason' && (
        <>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel shipment <strong>{shipmentId}</strong>?
            </DialogContentText>
            <Alert severity='info' sx={{ mt: 2 }}>
              Canceling this shipment will send the order to Prescription Review with a &apos;Ship
              Later&apos; reason and tag.
            </Alert>
            <TextField
              label='Reason (optional)'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              fullWidth
              size='small'
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' color='secondary' onClick={handleBack} disabled={isLoading}>
              Back
            </Button>
            <LoadingButton
              loading={isLoading}
              variant='contained'
              color='error'
              onClick={handleConfirmCancelShipmentOnly}
            >
              Yes, Cancel Shipment
            </LoadingButton>
          </DialogActions>
        </>
      )}

      {!hideOrderCancelOption && step === 'confirm' && (
        <>
          <DialogContent>
            <DialogContentText>
              {hideOrderCancelOption ? (
                <>
                  Are you sure you want to cancel shipment <strong>{shipmentId}</strong>? This
                  action cannot be undone.
                </>
              ) : (
                'Are you sure you want to cancel this order? This action cannot be undone. The order will be permanently canceled and cannot be processed further.'
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {hideOrderCancelOption ? (
              <Button
                variant='outlined'
                color='secondary'
                onClick={handleClose}
                disabled={isLoading}
              >
                No
              </Button>
            ) : (
              <Button
                variant='outlined'
                color='secondary'
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </Button>
            )}
            <LoadingButton
              loading={isLoading}
              variant='contained'
              color='error'
              onClick={
                hideOrderCancelOption ? handleConfirmCancelShipmentOnly : handleConfirmCancelOrder
              }
            >
              {hideOrderCancelOption ? 'Yes, Cancel Shipment' : 'Yes, Cancel Order'}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

CancelShipmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shipmentId: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hideOrderCancelOption: PropTypes.bool,
};

CancelShipmentDialog.defaultProps = {
  shipmentId: '',
  isLoading: false,
  hideOrderCancelOption: false,
};

export default CancelShipmentDialog;
